import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import { CommissionItem, ActiveCohortItem, Referrer, Payout } from '@api/types/commissions';
import {
  useLazyGetActiveCohortsQuery,
  useLazyGetCommissionsFiltersQuery,
  useLazyGetCommissionsChartQuery,
  useLazyGetCommissionsQuery,
  useLazyGetPayoutsQuery,
  useLazyCommissionsChartsDownloadQuery,
  useRefreshCommissionsMutation,
} from '@api/commissions';
import { setToastMessage } from '@stores/App/slice';
import { PayoutStatus as PayoutStatusEnum, TOAST_TYPES } from '@constants/constants';
import { PaginationConfig } from '@utils/types/PaginationConfig';
import { RootState } from '@stores/store';
import CommissionFilters from '../components/Commissions/Filters';
import Commissions from '../components/Commissions/Commissions';
import ActiveCohorts from '../components/Commissions/ActiveCohorts';
import CommissionsChart from '../components/Commissions/CommissionsChart/CommissionsChart';
import Payouts from '../components/Commissions/Payouts';

/* Constants */
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 10;

interface GlobalFilters {
  referrer: string;
}

const CommissionsDashboard = () => {
  const dispatch = useDispatch();

  /* STATE */
  const [commissions, setCommissions] = useState<CommissionItem[]>([]);
  const [activeCohorts, setActiveCohorts] = useState<ActiveCohortItem[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [payoutMonths, setPayoutMonths] = useState<string[]>([]);
  const [commissionsByReferrerChart, setCommissionsByReferrerChart] = useState<any>([]);
  const [commissionsByDealChart, setCommissionsByDealChart] = useState<any>([]);
  const [globalFilters, setGlobalFilters] = useState<GlobalFilters>({
    referrer: 'All',
  });
  const [activeCohortsPaginationDetails, setActiveCohortsPaginationDetails] =
    useState<PaginationConfig>({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
    });
  const [commissionsPaginationDetails, setCommissionsPaginationDetails] =
    useState<PaginationConfig>({
      pageNumber: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
    });
  const [payoutsPaginationDetails, setPayoutsPaginationDetails] = useState<PaginationConfig>({
    pageNumber: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [payoutsFilters, setPayoutsFilters] = useState<{
    status: PayoutStatusEnum[];
  }>({
    status: [PayoutStatusEnum.PENDING, PayoutStatusEnum.INITIATED],
  });
  const [selectedPayoutStatus, setSelectedPayoutStatus] = useState<PayoutStatusEnum | 'All'>(
    PayoutStatusEnum.PENDING,
  );
  const [selectedOptionsForReferrers, setSelectedOptionsForReferrers] = useState<string[]>([]);
  const [selectedOptionsForDeals, setSelectedOptionsForDeals] = useState<string[]>([]);

  /* APIS */
  const [getActiveCohorts, { isLoading: isActiveCohortsLoading }] = useLazyGetActiveCohortsQuery();
  const [getCommissions, { isLoading: isCommissionsLoading }] = useLazyGetCommissionsQuery();
  const [getCommissionsFilters] = useLazyGetCommissionsFiltersQuery();
  const [getCommissionsChart] = useLazyGetCommissionsChartQuery();
  const [getPayouts, { isLoading: isPayoutsLoading }] = useLazyGetPayoutsQuery();
  const [downloadCommissions] = useLazyCommissionsChartsDownloadQuery();
  const [refreshCommissions, { isLoading: isRefreshingCommissions }] =
    useRefreshCommissionsMutation();

  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  useEffect(() => {
    fetchCommissions();
    fetchActiveCohorts();
    fetchPayouts();
    fetchCharts('both');
    fetchFilters();
  }, []);

  const fetchCommissions = async (
    inputProps: {
      pageNumber?: number;
      pageSize?: number;
      referrer?: string;
    } = {},
  ) => {
    const props: {
      pageNumber: number;
      pageSize: number;
      referrer: string;
    } = {
      pageNumber: commissionsPaginationDetails.pageNumber || DEFAULT_PAGE_NUMBER,
      pageSize: DEFAULT_PAGE_SIZE,
      referrer: globalFilters.referrer,
      ...inputProps,
    };

    props.referrer = props.referrer === 'All' ? '' : props.referrer;

    try {
      const commissionsResponse = await getCommissions({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        referrerId: props.referrer,
      }).unwrap();

      setCommissions(commissionsResponse.data.items || []);
      setCommissionsPaginationDetails({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        total: commissionsResponse.data.totalElements || 0,
      });
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message,
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const fetchActiveCohorts = async (
    inputProps: {
      pageNumber?: number;
      pageSize?: number;
      referrer?: string;
    } = {},
  ) => {
    const props: {
      pageNumber: number;
      pageSize: number;
      referrer: string;
    } = {
      pageNumber: activeCohortsPaginationDetails.pageNumber || DEFAULT_PAGE_NUMBER,
      pageSize: DEFAULT_PAGE_SIZE,
      referrer: globalFilters.referrer,
      ...inputProps,
    };

    props.referrer = props.referrer === 'All' ? '' : props.referrer;

    try {
      const activeCohortsResponse = await getActiveCohorts({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        referrerId: props.referrer,
      }).unwrap();

      setActiveCohorts(activeCohortsResponse.data.items);
      setActiveCohortsPaginationDetails({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        total: activeCohortsResponse.data.totalElements,
      });
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message,
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const fetchPayouts = async (
    inputProps: {
      pageNumber?: number;
      pageSize?: number;
      status?: PayoutStatusEnum[];
      referrer?: string;
    } = {},
  ) => {
    const props: {
      pageNumber: number;
      pageSize: number;
      referrer?: string;
      status?: PayoutStatusEnum[];
    } = {
      pageNumber: payoutsPaginationDetails.pageNumber || DEFAULT_PAGE_NUMBER,
      pageSize: DEFAULT_PAGE_SIZE,
      referrer: globalFilters.referrer,
      status: payoutsFilters.status,
      ...inputProps,
    };

    props.referrer = props.referrer === 'All' ? '' : props.referrer;
    props.status = props.status && props.status.length > 0 ? props.status : [];

    setPayoutsFilters({
      status: props.status || [],
    });

    try {
      const response = await getPayouts({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        referrerId: props.referrer,
        status: props.status,
      }).unwrap();
      setPayouts(response.data.items);
      setPayoutsPaginationDetails({
        pageNumber: props.pageNumber,
        pageSize: props.pageSize,
        total: response.data.totalElements,
      });
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message,
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const fetchCharts = async (
    identifier: 'referrer' | 'deal' | 'both',
    inputProps: {
      referrer?: string;
      payoutMonth?: string[];
    } = {},
  ) => {
    const props: {
      referrer: string;
      payoutMonth?: string[];
    } = {
      referrer: globalFilters.referrer,
      payoutMonth: inputProps.payoutMonth || [],
      ...inputProps,
    };

    props.referrer = props.referrer === 'All' ? '' : props.referrer;
    if (props.payoutMonth?.length === 0) {
      props.payoutMonth = undefined;
    }

    try {
      const { data } = await getCommissionsChart({
        referrerId: props.referrer,
        payoutMonths: props.payoutMonth,
      }).unwrap();

      if (identifier === 'referrer') {
        setCommissionsByReferrerChart(data || []);
      } else if (identifier === 'deal') {
        setCommissionsByDealChart(data || []);
      } else if (identifier === 'both') {
        setCommissionsByReferrerChart(data || []);
        setCommissionsByDealChart(data || []);
      }
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message,
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await getCommissionsFilters().unwrap();
      setReferrers(
        response.data?.referrers
          .filter((r: Referrer) => r.name !== null)
          .sort((a: Referrer, b: Referrer) => a.name.localeCompare(b.name)) || [],
      );
      setPayoutMonths(response.data?.payoutMonths || []);
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message,
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const handleReferrerChange = (referrer: string) => {
    setGlobalFilters({
      ...globalFilters,
      referrer,
    });
    setSelectedOptionsForReferrers([]);
    setSelectedOptionsForDeals([]);
    fetchActiveCohorts({
      pageNumber: 0,
      referrer,
    });
    fetchCommissions({
      pageNumber: 0,
      referrer,
    });
    fetchCharts('both', {
      referrer: referrer,
      payoutMonth: [],
    });
    fetchPayouts({
      pageNumber: 0,
      referrer,
    });
  };

  const handleMarkAsDefaulted = () => {
    fetchActiveCohorts({
      pageNumber: 0,
      referrer: globalFilters.referrer,
    });

    fetchCommissions({
      pageNumber: 0,
      referrer: globalFilters.referrer,
    });
  };

  const handleDownload = async (payoutMonths: string[]) => {
    try {
      const queryObject: {
        referrerId: string;
        payoutMonths: string[];
      } = {
        referrerId: globalFilters.referrer === 'All' ? '' : globalFilters.referrer,
        payoutMonths: payoutMonths,
      };

      const response = await downloadCommissions(queryObject).unwrap();

      if (response) {
        const link = document.createElement('a');
        link.href = response.data.url || '';
        link.download = 'commissions.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Unable to download CSV, please try again later.',
        }),
      );
    }
  };

  const reloadData = () => {
    setGlobalFilters({
      referrer: 'All',
    });
    setSelectedPayoutStatus(PayoutStatusEnum.PENDING);
    setPayoutsFilters({
      status: [PayoutStatusEnum.PENDING, PayoutStatusEnum.INITIATED],
    });
    setSelectedOptionsForReferrers([]);
    setSelectedOptionsForDeals([]);

    fetchCommissions({
      pageNumber: 0,
      referrer: 'All',
    });
    fetchActiveCohorts({
      pageNumber: 0,
      referrer: 'All',
    });
    fetchPayouts({
      pageNumber: 0,
      status: [PayoutStatusEnum.PENDING, PayoutStatusEnum.INITIATED],
      referrer: 'All',
    });
    fetchCharts('both', {
      referrer: 'All',
      payoutMonth: [],
    });
    fetchFilters();
  };

  const handleRefreshCommissions = async () => {
    try {
      await refreshCommissions().unwrap();
      reloadData();
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message || 'Failed to recalculate data, please try again later.',
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  return (
    <Box
      id="commissions-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem 2rem 5rem',
        gap: 2,
        height: '100vh',
        overflow: 'auto',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h1"
          sx={{
            fontSize: '1.375rem',
            fontWeight: 700,
            lineHeight: '1.875rem',
          }}
          color="#6C6C6C"
        >
          Commissions
        </Typography>

        <Typography fontSize="0.875rem" fontWeight={700} lineHeight="1.125rem" color="#6C6C6C">
          $ = USD
        </Typography>
      </Stack>

      {/* Filters Section */}
      {userRole === 'ADMIN' && (
        <CommissionFilters
          referrers={referrers}
          referrer={globalFilters.referrer}
          onFilterChange={handleReferrerChange}
          isRefreshingCommissions={isRefreshingCommissions}
          onRefreshCommissions={handleRefreshCommissions}
        />
      )}

      {/* Table Section */}
      <Commissions
        isLoading={isCommissionsLoading}
        fetchCommissions={fetchCommissions}
        data={commissions}
        page={commissionsPaginationDetails.pageNumber}
        size={commissionsPaginationDetails.pageSize}
        total={commissionsPaginationDetails.total}
        filteredReferrer={globalFilters.referrer}
      />
      <ActiveCohorts
        page={activeCohortsPaginationDetails.pageNumber}
        size={activeCohortsPaginationDetails.pageSize}
        total={activeCohortsPaginationDetails.total}
        isLoading={isActiveCohortsLoading}
        fetchActiveCohorts={fetchActiveCohorts}
        data={activeCohorts}
        handleMarkAsDefaulted={handleMarkAsDefaulted}
        filteredReferrer={globalFilters.referrer}
      />
      <Payouts
        isLoading={isPayoutsLoading}
        fetchPayouts={fetchPayouts}
        data={payouts}
        page={payoutsPaginationDetails.pageNumber}
        size={payoutsPaginationDetails.pageSize}
        total={payoutsPaginationDetails.total}
        selectedPayoutStatus={selectedPayoutStatus}
        setSelectedPayoutStatus={setSelectedPayoutStatus}
        filteredReferrer={globalFilters.referrer}
        filteredPayoutStatus={payoutsFilters.status}
      />

      {/* Charts Section */}
      {userRole === 'ADMIN' && (
        <CommissionsChart
          xLabel="REFERRERS"
          yLabel="COMMISSIONS (USD)"
          isReferrers
          payoutOptions={payoutMonths}
          plotData={commissionsByReferrerChart}
          fetchCharts={(inputProps) => fetchCharts('referrer', inputProps)}
          selectedOptions={selectedOptionsForReferrers}
          setSelectedOptions={setSelectedOptionsForReferrers}
          handleDownload={handleDownload}
        />
      )}

      <CommissionsChart
        xLabel="DEALS"
        yLabel="COMMISSIONS (USD)"
        isReferrers={false}
        payoutOptions={payoutMonths}
        plotData={commissionsByDealChart}
        fetchCharts={(inputProps) => fetchCharts('deal', inputProps)}
        selectedOptions={selectedOptionsForDeals}
        setSelectedOptions={setSelectedOptionsForDeals}
        handleDownload={handleDownload}
      />
    </Box>
  );
};

export default CommissionsDashboard;
